import React, { useState } from 'react';
import { Upload, message, Button, Card, Space, Tabs, Alert, Divider } from 'antd';
import { UploadOutlined, FileOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useBulkUploadFournisseurs, useUploadFournisseursDBF } from '../../hooks/fourHooks';

const BulkUploadFournisseurs = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [dbfFile, setDbfFile] = useState(null);
  const [activeTab, setActiveTab] = useState('excel');
  
  const { handleBulkUpload: uploadExcel, loading: loadingExcel, error: errorExcel, uploadResult: resultExcel } = useBulkUploadFournisseurs();
  const { handleUpload: uploadDBF, loading: loadingDBF, error: errorDBF, uploadResult: resultDBF } = useUploadFournisseursDBF();

  const handleExcelUpload = async () => {
    if (!excelFile) {
      message.error('Please select an Excel file first!');
      return;
    }

    try {
      await uploadExcel(excelFile);
      message.success('Excel file uploaded successfully!');
      setExcelFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleDBFUpload = async () => {
    if (!dbfFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadDBF(dbfFile);
      message.success('DBF file uploaded successfully!');
      setDbfFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const excelProps = {
    onRemove: () => {
      setExcelFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel';
      
      if (!isExcel) {
        message.error('Please upload an Excel file (.xlsx or .xls)!');
        return Upload.LIST_IGNORE;
      }
      setExcelFile(file);
      return false;
    },
    fileList: excelFile ? [excelFile] : [],
    accept: '.xlsx,.xls',
  };

  const dbfProps = {
    onRemove: () => {
      setDbfFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setDbfFile(file);
      return false;
    },
    fileList: dbfFile ? [dbfFile] : [],
    accept: '.dbf,.DBF',
  };

  const tabItems = [
    {
      key: 'excel',
      label: (
        <span>
          <FileExcelOutlined /> Excel Upload
        </span>
      ),
      children: (
        <Card title="Upload Fournisseurs from Excel File" bordered>
          <Alert
            message="Excel File Upload Instructions"
            description={
              <div>
                <p><strong>Upload Excel file (.xlsx or .xls):</strong> Contains fournisseur data in Excel format</p>
                <p style={{ marginTop: 8, color: '#52c41a' }}>
                  <strong>Note:</strong> Excel files will update existing records if CODE_FRS matches
                </p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload {...excelProps}>
              <Button icon={<UploadOutlined />} disabled={loadingExcel}>
                Select Excel File
              </Button>
            </Upload>
            {excelFile && (
              <div>
                <FileExcelOutlined /> {excelFile.name}
              </div>
            )}
            <Button
              type="primary"
              onClick={handleExcelUpload}
              disabled={!excelFile || loadingExcel}
              loading={loadingExcel}
            >
              {loadingExcel ? 'Uploading...' : 'Upload Excel File'}
            </Button>
            {errorExcel && (
              <Alert message={errorExcel} type="error" showIcon />
            )}
            {resultExcel && (
              <Alert
                message="Upload Successful"
                description={
                  <div>
                    <p>Created: {resultExcel.created || 0}</p>
                    <p>Updated: {resultExcel.updated || 0}</p>
                    {resultExcel.errors && resultExcel.errors.length > 0 && (
                      <p>Errors: {resultExcel.errors.length}</p>
                    )}
                  </div>
                }
                type="success"
                showIcon
              />
            )}
          </Space>
        </Card>
      ),
    },
    {
      key: 'dbf',
      label: (
        <span>
          <FileOutlined /> DBF Upload
        </span>
      ),
      children: (
        <Card title="Upload Fournisseurs from DBF File" bordered>
          <Alert
            message="DBF File Upload Instructions"
            description={
              <div>
                <p><strong>Upload DBF file (.dbf):</strong> Contains fournisseur data in DBF format (typically FOURNISSEURS.dbf)</p>
                <p style={{ marginTop: 8, color: '#52c41a' }}>
                  <strong>Note:</strong> Any file name with .dbf or .DBF extension is accepted
                </p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload {...dbfProps}>
              <Button icon={<UploadOutlined />} disabled={loadingDBF}>
                Select DBF File
              </Button>
            </Upload>
            {dbfFile && (
              <div>
                <FileOutlined /> {dbfFile.name}
              </div>
            )}
            <Button
              type="primary"
              onClick={handleDBFUpload}
              disabled={!dbfFile || loadingDBF}
              loading={loadingDBF}
            >
              {loadingDBF ? 'Uploading...' : 'Upload DBF File'}
            </Button>
            {errorDBF && (
              <Alert message={errorDBF} type="error" showIcon />
            )}
            {resultDBF && (
              <Alert
                message="Upload Successful"
                description={resultDBF.message || 'Fournisseurs seeded successfully'}
                type="success"
                showIcon
              />
            )}
          </Space>
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </div>
  );
};

export default BulkUploadFournisseurs;